/**
 * BotworkClient — Browser SDK
 * Copied from infra/p2p/src/client/botwork-client.ts
 * Adapted imports for shell local types.
 */

import type {
  Agent,
  AgentDetails,
  TaskUpdate,
  RelayStatsResult,
  SearchAgentsParams,
  CreateTaskParams,
  CreateTaskResult,
  Signer,
  ConnectionState,
  BotworkClientOptions,
} from './types';

// ── Response type mapping ───────────────────────────────────────

const RESPONSE_MAP: Record<string, string> = {
  auth: 'auth_success',
  search_agents: 'search_results',
  get_agent: 'agent_details',
  get_relay_stats: 'relay_stats',
  get_agents: 'agents_list',
};

// ── Pending request tracker ─────────────────────────────────────

interface PendingRequest {
  resolve: (data: any) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

// ── BotworkClient ───────────────────────────────────────────────

export class BotworkClient {
  private ws: WebSocket | null = null;
  private gatewayUrl = '';
  private address: string | null = null;
  private state: ConnectionState = 'disconnected';
  private options: Required<BotworkClientOptions>;

  // Callbacks
  private connectionCallbacks: Array<(state: ConnectionState) => void> = [];
  private taskUpdateCallbacks: Array<(update: TaskUpdate) => void> = [];
  private agentOnlineCallbacks: Array<(agent: { address: string; peerId: string }) => void> = [];
  private agentOfflineCallbacks: Array<(agent: { address: string }) => void> = [];

  // Request-response tracking
  private pendingRequests = new Map<string, PendingRequest[]>();

  // Reconnect state
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private shouldReconnect = false;

  // Message queue (buffered while disconnected)
  private messageQueue: string[] = [];

  constructor(options: BotworkClientOptions = {}) {
    this.options = {
      autoReconnect: options.autoReconnect ?? true,
      maxReconnectDelay: options.maxReconnectDelay ?? 30_000,
      requestTimeout: options.requestTimeout ?? 30_000,
    };
  }

  // ── Connection ─────────────────────────────────────────────────

  connect(gatewayUrl: string): Promise<void> {
    this.gatewayUrl = gatewayUrl;
    this.shouldReconnect = this.options.autoReconnect;
    return this.doConnect();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState('disconnected');
  }

  isConnected(): boolean {
    return this.state === 'connected' || this.state === 'authenticated';
  }

  isAuthenticated(): boolean {
    return this.state === 'authenticated';
  }

  getAddress(): string | null {
    return this.address;
  }

  onConnectionChange(cb: (state: ConnectionState) => void): void {
    this.connectionCallbacks.push(cb);
  }

  // ── Authentication ─────────────────────────────────────────────

  async authenticate(signer: Signer): Promise<void> {
    const timestamp = Date.now();
    const message = `botwork-auth:${timestamp}`;
    const signature = await signer.signMessage(message);

    const response = await this.sendRequest(
      { type: 'auth', payload: { signature, address: signer.address, timestamp } },
      'auth_success',
    );

    this.address = response.address;
    this.setState('authenticated');
  }

  // ── Agent Discovery (no auth required) ─────────────────────────

  async searchAgents(params: SearchAgentsParams): Promise<Agent[]> {
    const response = await this.sendRequest(
      { type: 'search_agents', payload: params },
      'search_results',
    );

    return (response.agents || []).map(mapAnnouncementToAgent);
  }

  async getAgent(address: string): Promise<AgentDetails | null> {
    const response = await this.sendRequest(
      { type: 'get_agent', payload: { address } },
      'agent_details',
    );

    if (!response.agent) return null;
    return { ...mapAnnouncementToAgent(response.agent), timestamp: response.agent.timestamp };
  }

  async getRelayStats(): Promise<RelayStatsResult> {
    const response = await this.sendRequest(
      { type: 'get_relay_stats' },
      'relay_stats',
    );
    return { leaderboard: response.leaderboard || [] };
  }

  onAgentOnline(cb: (agent: { address: string; peerId: string }) => void): void {
    this.agentOnlineCallbacks.push(cb);
  }

  onAgentOffline(cb: (agent: { address: string }) => void): void {
    this.agentOfflineCallbacks.push(cb);
  }

  // ── Tasks (auth required) ──────────────────────────────────────

  async createTask(params: CreateTaskParams): Promise<CreateTaskResult> {
    const taskId = `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    this.send({
      type: 'create_task',
      task: {
        type: 'task-request',
        taskId,
        from: this.address || 'browser-client',
        payload: params.description,
        maxBudget: params.budget,
        timestamp: Date.now(),
      },
      agentAddr: params.agentPeerAddr,
    });

    return { taskId };
  }

  async approveTask(taskId: string): Promise<void> {
    this.send({ type: 'approve_task', taskId });
  }

  async disputeTask(taskId: string): Promise<void> {
    this.send({ type: 'dispute_task', taskId });
  }

  subscribeTask(taskId: string): void {
    this.send({ type: 'subscribe_task', taskId });
  }

  unsubscribeTask(taskId: string): void {
    this.send({ type: 'unsubscribe_task', taskId });
  }

  onTaskUpdate(cb: (update: TaskUpdate) => void): void {
    this.taskUpdateCallbacks.push(cb);
  }

  // ── Cleanup ────────────────────────────────────────────────────

  destroy(): void {
    this.disconnect();
    this.connectionCallbacks = [];
    this.taskUpdateCallbacks = [];
    this.agentOnlineCallbacks = [];
    this.agentOfflineCallbacks = [];
    this.rejectAllPending(new Error('Client destroyed'));
    this.messageQueue = [];
  }

  // ── Internal: Connection ───────────────────────────────────────

  private doConnect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState('connecting');

      const ws = new WebSocket(this.gatewayUrl);

      ws.onopen = () => {
        this.ws = ws;
        this.reconnectDelay = 1000;
        this.setState('connected');
        this.flushQueue();
        resolve();
      };

      ws.onclose = () => {
        const wasConnected = this.ws !== null;
        this.ws = null;
        this.setState('disconnected');

        if (wasConnected && this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };

      ws.onerror = () => {
        if (!this.ws) {
          reject(new Error('WebSocket connection failed'));
        }
      };

      ws.onmessage = (event) => {
        this.handleMessage(event.data as string);
      };
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      try {
        await this.doConnect();
      } catch {
        this.reconnectDelay = Math.min(
          this.reconnectDelay * 2,
          this.options.maxReconnectDelay,
        );
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      }
    }, this.reconnectDelay);
  }

  // ── Internal: Message handling ─────────────────────────────────

  private handleMessage(raw: string): void {
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }

    const { type } = msg;

    const pending = this.pendingRequests.get(type);
    if (pending && pending.length > 0) {
      const req = pending.shift()!;
      if (pending.length === 0) this.pendingRequests.delete(type);
      clearTimeout(req.timer);
      req.resolve(msg);
      return;
    }

    if (type === 'error') {
      for (const [key, queue] of this.pendingRequests) {
        if (queue.length > 0) {
          const req = queue.shift()!;
          if (queue.length === 0) this.pendingRequests.delete(key);
          clearTimeout(req.timer);
          req.reject(new Error(msg.message || 'Unknown error'));
          return;
        }
      }
      return;
    }

    if (type === 'task_update' || type === 'task_result') {
      const update: TaskUpdate = {
        taskId: msg.taskId,
        status: msg.status || (type === 'task_result' ? 'result_received' : msg.status),
        progress: msg.progress,
        result: msg.result,
        agentAddress: msg.agentAddress,
        splits: msg.splits,
      };
      for (const cb of this.taskUpdateCallbacks) {
        cb(update);
      }
      return;
    }

    if (type === 'agent_online') {
      for (const cb of this.agentOnlineCallbacks) {
        cb({ address: msg.payload?.address, peerId: msg.payload?.peerId });
      }
      return;
    }

    if (type === 'agent_offline') {
      for (const cb of this.agentOfflineCallbacks) {
        cb({ address: msg.payload?.address });
      }
      return;
    }
  }

  // ── Internal: Request-response ─────────────────────────────────

  private sendRequest(msg: any, expectedType: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        const queue = this.pendingRequests.get(expectedType);
        if (queue) {
          const idx = queue.findIndex((r) => r.timer === timer);
          if (idx >= 0) queue.splice(idx, 1);
          if (queue.length === 0) this.pendingRequests.delete(expectedType);
        }
        reject(new Error(`Request timeout waiting for ${expectedType}`));
      }, this.options.requestTimeout);

      const pending: PendingRequest = { resolve, reject, timer };

      let queue = this.pendingRequests.get(expectedType);
      if (!queue) {
        queue = [];
        this.pendingRequests.set(expectedType, queue);
      }
      queue.push(pending);

      this.send(msg);
    });
  }

  private send(msg: any): void {
    const raw = JSON.stringify(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(raw);
    } else {
      this.messageQueue.push(raw);
    }
  }

  private flushQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(this.messageQueue.shift()!);
    }
  }

  private rejectAllPending(error: Error): void {
    for (const [, queue] of this.pendingRequests) {
      for (const req of queue) {
        clearTimeout(req.timer);
        req.reject(error);
      }
    }
    this.pendingRequests.clear();
  }

  // ── Internal: State ────────────────────────────────────────────

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    for (const cb of this.connectionCallbacks) {
      cb(state);
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────

function mapAnnouncementToAgent(a: any): Agent {
  return {
    peerId: a.peerId,
    address: a.ethAddress,
    name: a.name,
    categories: a.categories || [],
    priceRange: a.priceRange || { min: 0, max: 0 },
    description: a.description || '',
    rating: a.rating || 0,
    totalTasks: a.totalTasks || 0,
    stakeAmount: a.stakeAmount || 0,
    online: true,
  };
}
